import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useEffect } from 'react';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { Stack, Dialog, DialogTitle, DialogContent, MenuItem, DialogActions, Button, Alert } from '@mui/material';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';

import { updateMaintenanceRecord } from '../store/maintenanceActions';
import { getServiceProviders } from '../../users/store/userActions';

const REPAIR_TYPES = [
  { id: 1, label: 'Preventive', value: 'PREVENTIVE' },
  { id: 2, label: 'Emergency', value: 'EMERGENCY' },
];
const REPAIR_VENUE = [
  { id: 1, label: 'Onsite', value: 'ONSITE' },
  { id: 2, label: 'Offsite', value: 'OFFSITE' },
];
// The value the provider select carries for "we are doing this ourselves".
const IN_HOUSE = 'IN_HOUSE';

EditMaintenance.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  record: PropTypes.object,
};

export default function EditMaintenance({ open, handleClose, record }) {
  const dispatch = useDispatch();
  const { serviceProviders } = useSelector((state) => state.userReducer);

  const EditMaintenanceSchema = Yup.object().shape({
    // mixed(), not number(): the select carries either a staff id or the
    // IN_HOUSE sentinel, and a number() cast silently blocks submit on both.
    provider: Yup.mixed().test(
      'provider-chosen',
      'Service provider is required',
      (value) => value !== undefined && value !== null && value !== ''
    ),
    repairType: Yup.string().required('Repair type is required'),
    repairVenue: Yup.string().required('Repair venue is required'),
    description: Yup.string(),
  });

  const methods = useForm({
    resolver: yupResolver(EditMaintenanceSchema),
    defaultValues: {
      provider: record?.in_house ? IN_HOUSE : record?.service_provider?.staff_id || '',
      repairType: record?.repair_type || '',
      repairVenue: record?.repair_venue || '',
      description: record?.description || '',
    },
  });

  const {
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;
  const values = watch();
  const switchingToInHouse = values.provider === IN_HOUSE && !record?.in_house;
  const switchingToProvider = values.provider !== IN_HOUSE && record?.in_house;

  useEffect(() => {
    dispatch(getServiceProviders({}));
  }, [dispatch]);

  const onSubmit = async (data) => {
    const payload = {
      in_house: data.provider === IN_HOUSE,
      repair_type: data.repairType,
      repair_venue: data.repairVenue,
      description: data.description || '',
    };
    // The API rejects a provider id alongside in_house.
    if (data.provider !== IN_HOUSE) payload.service_provider_id = data.provider;
    const result = await dispatch(updateMaintenanceRecord({ id: record.id, payload }));
    if (result?.ok) handleClose(true);
  };

  // Without an invalid handler a schema mismatch fails on a field with nowhere
  // to show it, and the button looks dead.
  const onInvalid = (formErrors) => {
    console.warn('Maintenance edit validation failed:', formErrors);
  };

  return (
    <Dialog maxWidth="sm" open={open} fullWidth keepMounted onClose={() => handleClose(false)}>
      <DialogTitle>Edit Maintenance Request</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <RHFSelect
              fullWidth
              name="provider"
              label="Service Provider"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false }}
            >
              <MenuItem value={IN_HOUSE}>In-house (our own team)</MenuItem>
              {serviceProviders?.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option?.user?.display_name}
                </MenuItem>
              ))}
            </RHFSelect>
            {switchingToInHouse && (
              <Alert severity="info">
                The job becomes in-house: no service provider acceptance is needed, and the branch does the work.
              </Alert>
            )}
            {switchingToProvider && (
              <Alert severity="warning">
                The new service provider will have to accept the job before work continues.
              </Alert>
            )}
            <RHFSelect
              fullWidth
              name="repairType"
              label="Repair Type"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false }}
            >
              {REPAIR_TYPES.map((option) => (
                <MenuItem key={option.id} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>
            <RHFSelect
              fullWidth
              name="repairVenue"
              label="Repair Venue"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false }}
            >
              {REPAIR_VENUE.map((option) => (
                <MenuItem key={option.id} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>
            <RHFTextField name="description" label="Description" placeholder="Optional" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" variant="contained" color="error" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <LoadingButton type="submit" size="small" variant="contained" loading={isSubmitting}>
            Save
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
